import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    numberAttribute,
    signal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25Icons } from "../../modules/s25-icon/s25.icon.const";
import { Event as EventItem } from "../../pojo/Event";
import { Item } from "../../pojo/Item";
import { EventService } from "../../services/event.service";
import { SpaceService } from "../../services/space.service";
import { ResourceService } from "../../services/resource.service";
import { OrganizationService } from "../../services/organization.service";
import { ContactService } from "../../services/contact.service";
import { UserprefService } from "../../services/userpref.service";
import { OlsService } from "../../services/ols.service";
import { AssignPolicyService } from "../../services/assign.policy.service";
import { Security } from "../../pojo/Security";
import { CopyObjectService } from "../../services/copy.object.service";
import { StateService } from "../../services/state.service";
import { FlsStore } from "../../stores/fls.store";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { S25LoadingInlineModule } from "../../modules/s25-loading/s25.loading.inline.module";
import { EventSummaryData } from "../s25-event-summary/s25.event.summary.component";
import { S25MoreActionsEditComponent } from "./edit/more.actions.edit.component";
import { ActionType, MoreActionsMenu, MoreActionsService } from "./more.actions.service";
import { S25MoreActionsRelatedComponent } from "../../modules/s25-more-actions/more-actions-related/s25.more.actions.related.component";
import { S25MoreActionsModule } from "../../modules/s25-more-actions/s25.more.actions.module";
import { S25DeleteModule } from "../../modules/s25-delete/s25.delete.module";
import { S25TaskModule } from "../../modules/s25-task/s25.task.module";
import { Bind } from "../../decorators/bind.decorator";
import { AngBridgeUtil } from "../../util/ang.bridge.util";
import { S25SubscribeComponent } from "../../modules/s25-more-actions/s25.subscribe.component";
import { LoginStore } from "../../stores/login.store";

type ActionItem = {
    name: ActionType;
    label: string;
    icon: S25Icons.Name;
    title: string;
};

@TypeManagerDecorator("s25-ng-more-actions-collapse")
@Component({
    selector: "s25-ng-more-actions-collapse",
    template: `
        @if (isLoadingPermissions()) {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        } @else if (hasAnyActions()) {
            <div class="maButtonWrapper">
                @if (!showMoreActions()) {
                    <button
                        class="moreActionsButton button-link aw-button"
                        (click)="toggleMoreActions()"
                        type="button"
                        aria-label="Show More Actions"
                        aria-expanded="false"
                    >
                        <s25-ng-icon [type]="'caretDown'" [ariaLabel]="false"></s25-ng-icon>
                        <span>More Actions</span>
                    </button>
                }
            </div>

            @if (showMoreActions()) {
                <div class="moreActionsCollapse">
                    <ul class="moreActionsNav">
                        @if (actionItems().length < 10) {
                            <li class="fillerLink"></li>
                        }

                        @for (item of actionItems(); track item.name) {
                            <li>
                                <button
                                    (click)="selectAction(item)"
                                    class="aw-button button-link"
                                    [class.active]="currentAction() === item"
                                    [id]="$first ? 'firstMoreAction' : null"
                                >
                                    <s25-ng-icon [type]="item.icon" [ariaLabel]="false" />
                                    <span class="moreActionsSpan">{{ item.label }}</span>
                                    <!--Make hidden bold text always there for consistent flex sizing-->
                                    <strong aria-hidden="true" class="moreActionsSpan boldSpaceHolder">{{
                                        item.label
                                    }}</strong>
                                </button>
                            </li>
                        }

                        <li>
                            <button
                                (click)="toggleMoreActions()"
                                class="aw-button button-link"
                                id="collapseMoreActions"
                                aria-label="Collapse More Actions"
                                aria-expanded="true"
                            >
                                <s25-ng-icon [type]="'caretUp'" [ariaLabel]="false"></s25-ng-icon>
                                <span class="moreActionsSpan">Collapse</span>
                            </button>
                        </li>
                    </ul>

                    @if (currentAction()) {
                        <div class="moreActionsBody">
                            <button
                                class="c-button c-button--flat c-button--close"
                                (click)="closeActionsBody()"
                                aria-label="Close More Actions"
                                role="button"
                                tabindex="0"
                            >
                                <s25-ng-icon [type]="'close'" [ariaLabel]="false"></s25-ng-icon>
                            </button>

                            <h2>{{ currentAction().title }}</h2>

                            @switch (currentAction().name) {
                                @case ("ownership") {
                                    <s25-ng-edit-ownership
                                        [eventId]="itemId()"
                                        [hasContactPerms]="hasViewContacts()"
                                    ></s25-ng-edit-ownership>
                                }
                                @case ("subscribe") {
                                    <s25-ng-subscribe
                                        [itemId]="itemId()"
                                        [itemTypeId]="itemTypeId()"
                                        [type]="'more-actions'"
                                    ></s25-ng-subscribe>
                                }
                                @case ("edit") {
                                    <s25-ng-more-actions-edit
                                        [eventId]="itemId()"
                                        [type]="'edit'"
                                        [closeFn]="closeActionsBody"
                                    ></s25-ng-more-actions-edit>
                                }
                                @case ("copy") {
                                    @if (itemTypeId() === 1) {
                                        <s25-ng-more-actions-edit
                                            [eventId]="itemId()"
                                            [type]="'clone'"
                                            [closeFn]="closeActionsBody"
                                            [eventData]="eventData()"
                                        ></s25-ng-more-actions-edit>
                                    } @else {
                                        <s25-ng-copy-object
                                            [itemTypeId]="itemTypeId()"
                                            [itemId]="itemId()"
                                        ></s25-ng-copy-object>
                                    }
                                }
                                @case ("create") {
                                    <s25-ng-loading-inline-static
                                        class="loading-action"
                                        text="Preparing to create {{ itemTypeName().toLowerCase() }}..."
                                    />
                                }
                                @case ("related") {
                                    <s25-ng-more-actions-related
                                        [itemTypeId]="itemTypeId()"
                                        [itemId]="itemId()"
                                        [addRemoveType]="'relatedEvents'"
                                    ></s25-ng-more-actions-related>
                                }
                                @case ("bindings") {
                                    <s25-ng-more-actions-related
                                        [itemTypeId]="itemTypeId()"
                                        [itemId]="itemId()"
                                        [addRemoveType]="'boundReservations'"
                                    ></s25-ng-more-actions-related>
                                }
                                @case ("print") {
                                    <s25-ng-print-object-report
                                        [itemId]="itemId()"
                                        [itemTypeId]="itemTypeId()"
                                        [reports]="reports.value() ?? []"
                                    ></s25-ng-print-object-report>
                                }
                                @case ("task") {
                                    <s25-ng-create-todo [eventId]="itemId()"></s25-ng-create-todo>
                                }
                                @case ("email") {
                                    <s25-ng-email-event
                                        [itemId]="itemId()"
                                        (closed)="closeActionsBody()"
                                    ></s25-ng-email-event>
                                }
                                @case ("delete") {
                                    @if (deleting()) {
                                        <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
                                    }
                                    <div class="moreActionsDelete">
                                        <s25-delete
                                            [hasToggle]="true"
                                            [deleteFn]="deleteFn"
                                            [cancelFn]="cancelDeleteFn"
                                            [confirmMsg]="'Delete this ' + itemTypeName().toLowerCase() + '?'"
                                            [textType]="itemTypeId()"
                                        ></s25-delete>
                                    </div>
                                }
                            }
                        </div>
                    }
                </div>
            }
        }
    `,
    styleUrls: ["./more.actions.collapse.component.scss"],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        S25LoadingInlineModule,
        S25MoreActionsEditComponent,
        S25MoreActionsRelatedComponent,
        S25MoreActionsModule,
        S25DeleteModule,
        S25TaskModule,
        S25IconComponent,
    ],
})
export class MoreActionsCollapseComponent implements MoreActionsMenu {
    readonly itemId = input.required({ transform: numberAttribute });
    readonly itemTypeId = input.required<Item.Id>();

    readonly showMoreActions = signal(false);
    readonly currentAction = signal<ActionItem | undefined>(undefined);
    readonly deleting = signal(false);
    readonly eventData = linkedSignal<unknown, EventSummaryData | undefined>({
        source: this.itemId,
        computation: () => undefined,
    });

    readonly fls = inject(FlsStore);
    readonly loginStore = inject(LoginStore);

    readonly subscribeComponent = viewChild(S25SubscribeComponent);

    readonly itemTypeName = computed(() => {
        switch (this.itemTypeId()) {
            case Item.Ids.Event:
                return "Event";
            case Item.Ids.Location:
                return "Location";
            case Item.Ids.Resource:
                return "Resource";
            case Item.Ids.Organization:
                return "Organization";
            case Item.Ids.Contact:
                return "Contact";
            default:
                return "Item";
        }
    });

    // Separate resources for each permission dependency
    readonly olsSecurity = s25Resource({
        if: () => !this.isContact(),
        params: () => ({
            itemId: this.itemId(),
            itemTypeId: this.itemTypeId(),
        }),
        loader: ({ params }): Promise<Security.accessItem[]> => {
            if (this.loginStore.loggedIn() && (this.isLocation() || this.isResource())) {
                return AssignPolicyService.getForCurrentUser(params.itemTypeId, [params.itemId], true);
            }
            return OlsService.getEditAccessItems([params.itemId], params.itemTypeId);
        },
    });

    readonly canCopy = s25Resource({
        if: () =>
            this.loginStore.loggedIn() &&
            (this.isOrganization() || this.isContact() || this.isLocation() || this.isResource()),
        params: () => ({ itemId: this.itemId(), itemTypeId: this.itemTypeId() }),
        loader: async ({ params }) => CopyObjectService.canCopy(params.itemTypeId, params.itemId),
    });

    readonly allowedEventStates = s25Resource({
        if: () => this.isEvent(),
        loader: async () => UserprefService.getAllowedStates(),
    });

    readonly eventItemData = s25Resource({
        if: () => this.isEvent() && this.loginStore.loggedIn(),
        params: () => ({ itemId: this.itemId() }),
        loader: async ({ params }) => EventService.getMoreActionsData(params.itemId),
    });

    readonly reports = s25Resource({
        if: () => this.loginStore.loggedIn() && (this.isEvent() || this.isLocation()),
        params: () => ({ itemId: this.itemId(), itemTypeId: this.itemTypeId() }),
        loader: async ({ params }) => {
            if (params.itemTypeId === Item.Ids.Event) {
                return EventService.getEventReports(params.itemId);
            } else if (params.itemTypeId === Item.Ids.Location) {
                return SpaceService.getSpaceReports();
            }
            return [];
        },
    });

    readonly isEvent = computed(() => this.itemTypeId() === Item.Ids.Event);
    readonly isLocation = computed(() => this.itemTypeId() === Item.Ids.Location);
    readonly isResource = computed(() => this.itemTypeId() === Item.Ids.Resource);
    readonly isOrganization = computed(() => this.itemTypeId() === Item.Ids.Organization);
    readonly isContact = computed(() => this.itemTypeId() === Item.Ids.Contact);

    private readonly editOls = computed(() => {
        const accessItems = this.olsSecurity.value();
        return S25Util.propertyGetVal(accessItems, "edit");
    });

    private readonly hasFullEdit = computed(() => this.editOls() === "F");
    private readonly hasEditAccess = computed(() => ["R", "C", "F"].includes(this.editOls()));

    private readonly canSchedule = computed(() => {
        if (!this.loginStore.loggedIn()) {
            return false;
        }
        const olsSecurity = this.olsSecurity.value();
        return (
            S25Util.propertyGetVal(olsSecurity, "assignPerm") !== "noRequest" ||
            !!S25Util.propertyGetVal(olsSecurity, "assignOverride")
        );
    });

    private readonly eventPerms = computed((): EventItem.Permissions.CopyEditEmail => {
        if (!this.loginStore.loggedIn() || !this.isEvent()) {
            return { hasCopyEvent: false, hasEditEvent: false, hasEmail: false, hasRelatedEvents: false };
        }
        return EventService.formEventCopyEditEmailPerms(
            true,
            this.fls.value(),
            this.editOls(),
            this.eventItemData.value(),
            this.allowedEventStates.value(),
        );
    });

    readonly hasCopyObject = computed(() => this.canCopy.value() ?? false);

    readonly hasManageRelatedEvents = computed(
        () => this.loginStore.loggedIn() && this.isEvent() && this.hasEditAccess() && this.fls.hasFull("EVENT_ACTION"),
    );

    readonly hasDeleteEvent = computed(
        () =>
            this.loginStore.loggedIn() &&
            this.isEvent() &&
            this.fls.hasFull("EVENT_DELETE") &&
            (this.fls.hasFull("EVENT_PERM") || this.hasFullEdit()),
    );

    readonly hasDeleteLocation = computed(
        () =>
            this.loginStore.loggedIn() &&
            this.isLocation() &&
            this.fls.hasFull("SPACE_DEL") &&
            (this.fls.hasFull("SPACE_PERM") || this.hasFullEdit()),
    );

    readonly hasDeleteResource = computed(
        () =>
            this.loginStore.loggedIn() &&
            this.isResource() &&
            this.fls.hasFull("RESOURCE_DEL") &&
            (this.fls.hasFull("RESOURCE_PERM") || this.hasFullEdit()),
    );

    readonly hasDeleteOrganization = computed(
        () =>
            this.loginStore.loggedIn() &&
            this.isOrganization() &&
            this.fls.hasFull("CU_ACCOUNT") &&
            this.fls.hasFull("CU_ACCT_DEL") &&
            (this.fls.hasFull("CU_PERM") || this.hasFullEdit()),
    );

    readonly hasDeleteContact = computed(
        () => this.loginStore.loggedIn() && this.isContact() && this.fls.hasFull("CU_CONT_DEL"),
    );

    readonly hasViewContacts = computed(() => this.loginStore.loggedIn() && this.fls.hasRead("CU_CONTACT"));

    readonly hasCreateTodo = computed(
        () => this.loginStore.loggedIn() && this.isEvent() && this.fls.hasEdit("CREATE_TODO"),
    );

    readonly hasPrintReport = computed(() => this.fls.hasRead("REP_LIST") && this.reports.value()?.length);

    readonly hasTakeOwnership = computed(
        () =>
            this.loginStore.loggedIn() &&
            this.isEvent() &&
            (this.hasFullEdit() || this.fls.hasFull("EVENT_PERM")) &&
            this.fls.hasFull("EVENT_SECURITY"),
    );

    readonly hasCreateEvent = computed(
        () =>
            this.loginStore.loggedIn() &&
            this.fls.hasFull("SPEEDBOOK") &&
            (this.isOrganization() || this.canSchedule()),
    );

    readonly isLoadingPermissions = computed(
        () =>
            !this.loginStore.init() ||
            this.loginStore.loggingIn() ||
            this.olsSecurity.resource.isLoading() ||
            this.canCopy.resource.isLoading() ||
            this.allowedEventStates.resource.isLoading() ||
            this.eventItemData.resource.isLoading() ||
            this.reports.resource.isLoading(),
    );

    readonly actionItems = computed((): ActionItem[] => {
        const items: ActionItem[] = [];

        if (this.eventPerms().hasEditEvent) {
            items.push({ name: "edit", label: "Edit Event", icon: "edit2", title: "Edit Event" });
        }

        if (this.eventPerms().hasCopyEvent) {
            items.push({ name: "copy", label: "Copy Event", icon: "copy2", title: "Copy Event" });
        }

        if (this.hasCopyObject()) {
            items.push({
                name: "copy",
                label: `Copy ${this.itemTypeName()}`,
                icon: "copy2",
                title: `Copy ${this.itemTypeName()}`,
            });
        }

        if (this.hasManageRelatedEvents()) {
            items.push(
                {
                    name: "related",
                    label: "Related Events",
                    icon: "calendar",
                    title: "Manage Related Events",
                },
                {
                    name: "bindings",
                    label: "Manage Bindings",
                    icon: "share3",
                    title: "Manage Bindings",
                },
            );
        }

        if (this.hasCreateTodo()) {
            items.push({
                name: "task",
                label: "Create Task",
                icon: "clipboard",
                title: "Create Task",
            });
        }

        if (this.hasPrintReport()) {
            items.push({
                name: "print",
                label: "Print Report",
                icon: "printer",
                title: `Print a Report for This ${this.itemTypeName()}`,
            });
        }

        if (this.eventPerms().hasEmail) {
            items.push({ name: "email", label: "Email Event", icon: "mail", title: "Email Event" });
        }

        if (
            this.hasDeleteEvent() ||
            this.hasDeleteLocation() ||
            this.hasDeleteResource() ||
            this.hasDeleteOrganization() ||
            this.hasDeleteContact()
        ) {
            items.push({
                name: "delete",
                label: `Delete ${this.itemTypeName()}`,
                icon: "trash2",
                title: `Delete ${this.itemTypeName()}`,
            });
        }

        if (this.hasCreateEvent()) {
            items.push({
                name: "create",
                label: "Create Event",
                icon: "calendar",
                title: "Create Event",
            });
        }

        if (this.hasTakeOwnership()) {
            items.push({
                name: "ownership",
                label: "Edit Ownership",
                icon: "userCheck",
                title: "Edit Ownership of this Event",
            });
        }

        if (this.isEvent() || this.isLocation()) {
            const title = this.isEvent() ? "Subscribe to this Event" : "Subscribe to Events in this Location";
            items.push({ name: "subscribe", label: "Subscribe", icon: "checkCircle", title });
        }

        return items;
    });

    readonly hasAnyActions = computed(() => !!this.actionItems().length);

    readonly $scope = AngBridgeUtil.make$Scope();

    constructor() {
        effect((onCleanup) => {
            MoreActionsService.register(this);
            onCleanup(() => MoreActionsService.unregister(this));
        });
        this.$scope.$on("eventData", (event, data: EventSummaryData) => this.eventData.set(data));

        effect(() => {
            // subscribe component is weird, so uh, we call getSubscribe as soon as it renders.
            // TODO: refactor that
            void this.subscribeComponent()?.getSubscribe();
        });

        // Handle "create" action - navigate to ROSE
        effect(() => {
            if (this.currentAction()?.name === "create") {
                StateService.gotoRose({
                    itemId: this.itemId(),
                    itemTypeId: this.itemTypeId(),
                    isNew: true,
                }).finally(() => {
                    this.currentAction.update(() => {
                        if (this.currentAction()?.name === "create") {
                            return undefined;
                        }
                        return this.currentAction();
                    });
                    this.showMoreActions.set(false);
                });
            }
        });
    }

    // Delete function
    @Bind
    async deleteFn() {
        this.deleting.set(true);
        try {
            let resp: { success: boolean };
            const typeId = this.itemTypeId();

            switch (typeId) {
                case Item.Ids.Event:
                    resp = await EventService.deleteEvent(this.itemId());
                    break;
                case Item.Ids.Location:
                    resp = await SpaceService.deleteLocation(this.itemId());
                    break;
                case Item.Ids.Resource:
                    resp = await ResourceService.deleteResource(this.itemId());
                    break;
                case Item.Ids.Organization:
                    resp = await OrganizationService.deleteOrganization(this.itemId());
                    break;
                case Item.Ids.Contact:
                    resp = await ContactService.deleteContact(this.itemId());
                    break;
                default:
                    throw new Error(`Cannot delete item type ${typeId}`);
            }

            if (resp.success) {
                StateService.closeCurrent();
            } else {
                S25Util.showError(
                    resp,
                    `There was a problem with your request. The ${this.itemTypeName().toLowerCase()} was not deleted.`,
                );
            }
        } finally {
            this.deleting.set(false);
        }
    }

    @Bind
    cancelDeleteFn() {
        this.currentAction.set(undefined);
    }

    toggleMoreActions() {
        this.showMoreActions.update((val) => !val);
        const isLoggedIn = this.loginStore.loggedIn();
        if (this.showMoreActions() && isLoggedIn && (this.isEvent() || this.isLocation())) {
            setTimeout(() => {
                document.getElementById("firstMoreAction")?.focus();
            }, 1);
        }
    }

    /**
     * Select an action and open the collapse
     */
    selectAction(action?: ActionType | ActionItem) {
        let newAction: ActionItem | undefined;
        if (!action) {
            newAction = undefined;
        } else if (typeof action === "string") {
            newAction = this.actionItems().find((item) => item.name === action);
            if (!newAction) {
                throw new Error(`Action ${action} not found`);
            }
        } else if (this.actionItems().includes(action)) {
            newAction = action;
        } else {
            console.error(`Action not found`, action);
            throw new Error(`Action not found`);
        }
        if (newAction) {
            this.showMoreActions.set(true);
        }
        this.currentAction.set(newAction);
    }

    @Bind
    closeActionsBody() {
        this.currentAction.set(undefined);
        window.scrollTo(0, 0);
    }

    private getOlsChecks(itemTypeId: Item.Id): string[] {
        switch (itemTypeId) {
            case Item.Ids.Event:
                return ["edit", "own"];
            case Item.Ids.Location:
            case Item.Ids.Resource:
            case Item.Ids.Organization:
                return ["edit"];
            default:
                return [];
        }
    }
}
